/**
 * The device's side of the centralised sign-in.
 *
 * Three jobs, and it is the only module that does any of them:
 *
 * 1. **Talk to the auth API.** `/api/login` to sign in, `/api/me` to ask the
 *    server whether the account is still what it was, `/api/users` for the
 *    directory screen.
 * 2. **Hold the token.** In `localStorage`, so the next launch is signed in
 *    already, and on every request as an `Authorization: Bearer` — the server
 *    also sets an `HttpOnly` cookie, and the bearer is what makes a deployment
 *    pointed at another origin (`VITE_API_BASE_URL`) work too.
 * 3. **Land the identity in Dexie.** The rest of the application reads a local
 *    `User`, and it must keep reading one: the permissions engine, the audit
 *    ledger and every store guard are built on it. A cloud sign-in therefore
 *    ends by writing the directory's answer into the local directory, and the
 *    app carries on exactly as it did.
 *
 * **Offline-first is unchanged.** Every function here fails soft: no answer
 * means no signal, and the caller falls back to the local flow it always had.
 * The one thing that is *not* soft is a refusal — a server that says "this
 * number is not registered" or "this account is disabled" is believed, because
 * being able to talk past it is the whole thing the directory exists to stop.
 */
import { db } from './index';
import { APP_CONFIG } from '../core/config';
import {
  AUTH_TOKEN_KEY,
  CLOUD_AUTH_MESSAGES,
  accountRoleForCloudRole,
  authEndpoint,
  isCloudRole,
} from '../core/cloud-auth';
import type { CloudLoginResult, CloudRole, CloudUser } from '../core/cloud-auth';
import { normalizePhone } from '../core/phone';
import { DEFAULT_USER_PREFERENCES } from '../types/user';
import type { User } from '../types/user';

/** How long one auth call may take before it is treated as no answer at all. */
export const AUTH_TIMEOUT_MS = 12_000;

/** The company a cloud account with no roster record behind it is filed under. */
const DEFAULT_COMPANY = 'פלוגת שינוע 99';

const nowIso = (): string => new Date().toISOString();

// ── The token ────────────────────────────────────────────────────────

/**
 * `localStorage` is reached through a guard because it is not always there: a
 * private window with storage disabled throws on access, and an auth layer that
 * threw on boot would take the whole shell down with it. Same rule as
 * `defaultStorage()` in `src/core/session-cache.ts`.
 */
function tokenStorage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

export function readAuthToken(): string | null {
  try {
    return tokenStorage()?.getItem(AUTH_TOKEN_KEY) ?? null;
  } catch {
    return null;
  }
}

export function writeAuthToken(token: string): void {
  try {
    tokenStorage()?.setItem(AUTH_TOKEN_KEY, token);
  } catch {
    // A device that cannot keep the token simply signs in again next launch.
  }
}

export function clearAuthToken(): void {
  try {
    tokenStorage()?.removeItem(AUTH_TOKEN_KEY);
  } catch {
    // Nothing to clear is the same outcome as having cleared it.
  }
}

// ── Transport ────────────────────────────────────────────────────────

/** What one call to the auth API came back with. */
export interface ApiReply<T> {
  /** The server answered — whatever it said. `false` means it never did. */
  reached: boolean;
  ok: boolean;
  status: number;
  data: T | null;
  error: string | null;
}

/**
 * One authenticated call, with a deadline.
 *
 * Never throws. "The server refused" and "there is no server" are different
 * answers and every caller here has to tell them apart, so both come back as a
 * value rather than one as an exception.
 */
export async function callAuthApi<T>(
  path: string,
  init: { method?: string; body?: unknown; token?: string | null } = {},
): Promise<ApiReply<T>> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), AUTH_TIMEOUT_MS);
  const token = init.token === undefined ? readAuthToken() : init.token;

  try {
    const response = await fetch(authEndpoint(APP_CONFIG.apiBaseUrl, path), {
      method: init.method ?? (init.body === undefined ? 'GET' : 'POST'),
      headers: {
        ...(init.body === undefined ? {} : { 'Content-Type': 'application/json' }),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: init.body === undefined ? undefined : JSON.stringify(init.body),
      // The server also sets an HttpOnly cookie; on the same origin it rides along.
      credentials: 'same-origin',
      signal: controller.signal,
    });

    let payload: unknown = null;
    try {
      payload = await response.json();
    } catch {
      payload = null;
    }
    const body = (payload ?? {}) as { error?: string };

    return {
      reached: true,
      ok: response.ok,
      status: response.status,
      data: response.ok ? ((payload ?? null) as T | null) : null,
      error: response.ok ? null : body.error ?? `HTTP ${response.status}`,
    };
  } catch {
    return { reached: false, ok: false, status: 0, data: null, error: CLOUD_AUTH_MESSAGES.unreachable };
  } finally {
    clearTimeout(timer);
  }
}

// ── Landing a cloud identity in the local directory ───────────────────

/**
 * Writes the directory's answer into Dexie and hands back the local account.
 *
 * Matched **by phone, not by id**. The installation may already hold a record
 * for that number — the seeded owner does, under an id the safety guards in
 * `src/db/auth-store.ts` name explicitly — and creating a second row for the
 * same person would leave `findUserByPhone()` picking between two accounts and
 * the ledger pointing at whichever one it happened to get. The local id is
 * therefore kept, and the cloud id is recorded alongside it.
 *
 * What the cloud owns: the name, the role and the fact that the account may
 * sign in. What it leaves alone: the personal number, the company, the platoon
 * and the per-account permission flags — none of them are in the directory, and
 * a sign-in must not erase what the management screens put there.
 */
export async function applyCloudUser(cloudUser: CloudUser): Promise<User> {
  const phone = normalizePhone(cloudUser.phone);
  const existing =
    (await db.users.where('phone').equals(phone).first()) ??
    (await db.users.get(cloudUser.id)) ??
    null;

  const user: User = {
    ...(existing ?? {
      id: cloudUser.id,
      personalNumber: '',
      company: DEFAULT_COMPANY,
      platoon: '',
      createdAt: nowIso(),
      preferences: { ...DEFAULT_USER_PREFERENCES },
    }),
    phone,
    fullName: cloudUser.name,
    role: accountRoleForCloudRole(cloudUser.role),
    status: 'active',
    isApproved: true,
    cloudId: cloudUser.id,
    cloudRole: isCloudRole(cloudUser.role) ? cloudUser.role : undefined,
  } as User;

  await db.users.put(user);
  return user;
}

// ── Sign in ──────────────────────────────────────────────────────────

/** What a sign-in attempt produced. */
export type CloudLoginOutcome =
  /** The directory let them in. The local account is already written. */
  | { kind: 'signed-in'; user: User; cloud: CloudUser; token: string }
  /** The server answered and said no. `message` is what to show. */
  | { kind: 'rejected'; status: number; message: string }
  /** No answer — no signal, or a deployment with no functions behind it. */
  | { kind: 'unreachable' };

/**
 * `POST /api/login` — phone plus the shared group password.
 *
 * On success the token is stored and the identity is landed in Dexie, so the
 * caller has a real local `User` to open a session with and nothing else to do.
 */
export async function cloudLogin(phone: string, password: string): Promise<CloudLoginOutcome> {
  const reply = await callAuthApi<CloudLoginResult>('login', {
    method: 'POST',
    body: { phone: normalizePhone(phone), password },
    token: null,
  });

  if (!reply.reached) return { kind: 'unreachable' };
  if (!reply.ok || !reply.data?.token || !reply.data?.user) {
    return {
      kind: 'rejected',
      status: reply.status,
      message: reply.error ?? CLOUD_AUTH_MESSAGES.rejected,
    };
  }

  writeAuthToken(reply.data.token);
  const user = await applyCloudUser(reply.data.user);
  return { kind: 'signed-in', user, cloud: reply.data.user, token: reply.data.token };
}

/** Drops the session cookie server-side and the token here. */
export async function cloudLogout(): Promise<void> {
  await callAuthApi('logout', { method: 'POST' });
  clearAuthToken();
}

// ── Staying signed in ────────────────────────────────────────────────

/** What the server said when asked whether this session is still good. */
export type CloudSessionVerdict =
  /** Still valid. The local account has been refreshed from the directory. */
  | { kind: 'valid'; user: User; cloud: CloudUser }
  /** The directory has disabled or removed the account — sign out. */
  | { kind: 'revoked'; message: string }
  /** No token on this device; it never signed in against the cloud. */
  | { kind: 'none' }
  /** No answer. Say nothing and carry on locally. */
  | { kind: 'unreachable' };

/**
 * `GET /api/me` — the server's verdict on this device's session.
 *
 * This is what makes the thirty-day local session server-authoritative: the
 * account's standing is re-read out of D1, so revoking somebody takes effect on
 * their next launch rather than on their next login. A device that cannot reach
 * the server gets `unreachable` and keeps working — being out of signal is not
 * grounds for locking somebody out of their own phone.
 */
export async function revalidateCloudSession(): Promise<CloudSessionVerdict> {
  const token = readAuthToken();
  if (!token) return { kind: 'none' };

  const reply = await callAuthApi<{ user: CloudUser }>('me', { token });
  if (!reply.reached) return { kind: 'unreachable' };

  if (!reply.ok) {
    clearAuthToken();
    return { kind: 'revoked', message: reply.error ?? CLOUD_AUTH_MESSAGES.disabled };
  }
  if (!reply.data?.user) return { kind: 'unreachable' };

  const user = await applyCloudUser(reply.data.user);
  return { kind: 'valid', user, cloud: reply.data.user };
}

// ── The directory screen ─────────────────────────────────────────────

/** What a directory call produced — the rows, or the sentence to show instead. */
export interface DirectoryResult<T> {
  ok: boolean;
  data: T | null;
  error: string | null;
}

const directory = <T>(reply: ApiReply<T>): DirectoryResult<T> => ({
  ok: reply.ok,
  data: reply.data,
  error: reply.ok ? null : reply.error ?? CLOUD_AUTH_MESSAGES.unreachable,
});

export async function listCloudUsers(): Promise<DirectoryResult<CloudUser[]>> {
  const reply = await callAuthApi<{ users: CloudUser[] }>('users');
  return { ...directory(reply), data: reply.data?.users ?? null };
}

export async function createCloudUser(draft: {
  phone: string;
  name: string;
  role: CloudRole;
}): Promise<DirectoryResult<CloudUser>> {
  const reply = await callAuthApi<{ user: CloudUser }>('users', {
    method: 'POST',
    body: { ...draft, phone: normalizePhone(draft.phone) },
  });
  return { ...directory(reply), data: reply.data?.user ?? null };
}

export async function updateCloudUser(
  id: string,
  change: { name?: string; role?: CloudRole; isActive?: boolean },
): Promise<DirectoryResult<CloudUser>> {
  const reply = await callAuthApi<{ user: CloudUser }>(`users/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: change,
  });
  return { ...directory(reply), data: reply.data?.user ?? null };
}

export async function deleteCloudUser(id: string): Promise<DirectoryResult<{ id: string }>> {
  const reply = await callAuthApi<{ id: string }>(`users/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
  return directory(reply);
}
